import {
  useLayoutEffect,
  useState,
  useRef,
  type ComponentPropsWithRef,
} from "react";
import {
  Root as TabsRoot,
  List as TabsListPrimitive,
  Trigger as TabsTriggerPrimitive,
  Content as TabsContentPrimitive,
} from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const Tabs = TabsRoot;

const tabsListVariants = cva(
  "inline-flex h-9 items-center justify-center text-foreground/45",
  {
    variants: {
      variant: {
        default: "rounded-sm bg-black/5 p-1",
        line: "w-full justify-start gap-4 rounded-none bg-transparent p-0",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const tabsTriggerVariants = cva(
  `
    inline-flex items-center justify-center text-sm font-medium
    whitespace-nowrap ring-offset-background transition-all
    focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
    focus-visible:outline-none
    disabled:pointer-events-none disabled:opacity-50
  `,
  {
    variants: {
      variant: {
        default: `
          rounded-sm px-3 py-1
          data-[state=active]:bg-background data-[state=active]:text-foreground
          data-[state=active]:shadow-sm
        `,
        line: `
          rounded-none bg-transparent px-4 pt-3.5 pb-3 shadow-none
          data-[state=active]:text-foreground data-[state=active]:shadow-none
        `,
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface TabsListProps
  extends
    ComponentPropsWithRef<typeof TabsListPrimitive>,
    VariantProps<typeof tabsListVariants> {
  /** When variant is "line", pass the current tab value to enable the sliding indicator animation. */
  value?: string;
  /**
   * When variant is "line", change this when the set of triggers changes so the
   * underline remasures even if `value` stayed the same (e.g. a tab was removed).
   */
  layoutKey?: string;
}

const TabsList = ({
  className,
  variant = "default",
  value,
  layoutKey,
  ref,
  ...props
}: TabsListProps) => {
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
  } | null>(null);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const innerRef = useRef<HTMLDivElement>(null);
  const prevLayoutKeyRef = useRef(layoutKey);

  // Intentional second commit: read layout after Radix paints active trigger (variant="line").
  /* eslint-disable @eslint-react/set-state-in-effect -- measure active trigger; snap when layoutKey changes */
  useLayoutEffect(() => {
    if (variant !== "line" || !innerRef.current) return;
    const listEl = innerRef.current;
    const layoutChanged = prevLayoutKeyRef.current !== layoutKey;
    prevLayoutKeyRef.current = layoutKey;

    // File / tab-set changes should snap; same-layout value changes may animate.
    if (layoutChanged) {
      setTransitionEnabled(false);
    }

    const active = listEl.querySelector<HTMLElement>("[data-state=active]");
    if (active) {
      setIndicatorStyle({
        left: active.offsetLeft,
        width: active.offsetWidth,
      });
    } else {
      setIndicatorStyle(null);
    }
  }, [variant, value, layoutKey]);

  useLayoutEffect(() => {
    if (transitionEnabled) return;
    const frameId = requestAnimationFrame(() => {
      setTransitionEnabled(true);
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [transitionEnabled, indicatorStyle]);
  /* eslint-enable @eslint-react/set-state-in-effect */

  return (
    <TabsListPrimitive
      ref={(node) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      className={cn("relative", tabsListVariants({ variant }), className)}
      {...props}
    >
      {props.children}
      {variant === "line" && indicatorStyle != null && (
        <span
          className={cn(
            "absolute bottom-0 h-0.5 bg-primary",
            transitionEnabled &&
              "transition-[left,width] duration-200 ease-out",
          )}
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
          aria-hidden
        />
      )}
    </TabsListPrimitive>
  );
};
TabsList.displayName = TabsListPrimitive.displayName;

export interface TabsTriggerProps
  extends
    ComponentPropsWithRef<typeof TabsTriggerPrimitive>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = ({
  className,
  variant = "default",
  ref,
  ...props
}: TabsTriggerProps) => (
  <TabsTriggerPrimitive
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  />
);
TabsTrigger.displayName = TabsTriggerPrimitive.displayName;

const TabsContent = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof TabsContentPrimitive>) => (
  <TabsContentPrimitive
    ref={ref}
    className={cn(
      `
        mt-4 ring-offset-background
        focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
        focus-visible:outline-none
      `,
      className,
    )}
    {...props}
  />
);
TabsContent.displayName = TabsContentPrimitive.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
